import { useParams, Link } from "react-router-dom";
import { articlesBySlug, articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import SEOHead from "@/components/feature/SEOHead";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articlesBySlug[slug] : null;

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章不存在</h2>
        <p className="text-sm text-foreground-500 mb-6">请检查网址或返回首页</p>
        <Link to="/" className="text-sm text-accent-500 hover:underline cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
    .slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `https://17cyqc.com/article/${slug}/#article`,
        headline: article.title,
        description: article.excerpt,
        author: {
          "@type": "Person",
          name: article.author,
        },
        datePublished: article.publishedAt,
        image: article.coverImage,
        publisher: { "@id": "https://17cyqc.com/#organization" },
        isPartOf: { "@id": "https://17cyqc.com/#website" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17cyqc.com" },
          ...(category
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: category.name,
                  item: `https://17cyqc.com/category/${category.slug}`,
                },
              ]
            : []),
          {
            "@type": "ListItem",
            position: category ? 3 : 2,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        meta={{
          title: `${article.title} - 17c一起草国`,
          description: article.excerpt,
          keywords: `${article.tags.join(", ")}, 17c一起草国, 免费阅读, 完整版`,
          canonical: `https://17cyqc.com/article/${slug}`,
          ogTitle: article.title,
          ogDescription: article.excerpt,
          ogUrl: `https://17cyqc.com/article/${slug}`,
          ogType: "article",
          twitterCard: "summary_large_image",
          jsonLd,
        }}
      />

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <header className="mb-8">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="inline-block text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium mb-4 hover:bg-accent-200 transition-colors cursor-pointer"
            >
              {category.name}
            </Link>
          )}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {article.viewCount.toLocaleString()} 阅读
            </span>
          </div>
          {article.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </header>

        <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
            title={`${article.title} - 17c一起草国`}
          />
        </div>

        <div
          className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
            [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
            [&_p]:mb-5 [&_p]:leading-[1.8]
            [&_strong]:text-foreground-950
            "
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <footer className="mt-12 pt-8 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-500 text-sm font-bold">
                {article.author.slice(0, 1)}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                <p className="text-xs text-foreground-500">内容创作者</p>
              </div>
            </div>
          </div>
        </footer>
      </article>

      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h3 className="text-lg font-bold text-foreground-950 mb-6">相关推荐</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-accent-200 transition-colors cursor-pointer"
                  data-product-shop=""
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={`${ra.title} - 17c一起草国`}
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-accent-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <span className="text-xs text-foreground-500">{ra.publishedAt}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}